/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';

interface Props {
  columns: string[];
  matrix: number[][];
}

function getColor(value: number): string {
  // Diverging color: blue (-1) → white (0) → red (+1)
  const clamped = Math.max(-1, Math.min(1, value));
  if (clamped >= 0) {
    const intensity = Math.round(clamped * 200);
    return `rgba(211, 47, 47, ${intensity / 255})`;
  }
  const intensity = Math.round(-clamped * 200);
  return `rgba(25, 118, 210, ${intensity / 255})`;
}

export default function CorrelationHeatmap({ columns, matrix }: Props) {
  const size = columns.length;
  const cellSize = Math.max(28, Math.min(48, 400 / size));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Correlation Matrix
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        {/* Column labels */}
        <Box sx={{ display: 'flex', ml: `${cellSize + 4}px` }}>
          {columns.map((col) => (
            <Box
              key={col}
              sx={{
                width: cellSize,
                minWidth: cellSize,
                textAlign: 'center',
                fontSize: 9,
                transform: 'rotate(-45deg)',
                transformOrigin: 'center bottom',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                height: cellSize,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
            >
              {col}
            </Box>
          ))}
        </Box>
        {/* Matrix rows */}
        {matrix.map((row, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: cellSize + 4,
                minWidth: cellSize + 4,
                fontSize: 9,
                textAlign: 'right',
                pr: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {columns[i]}
            </Box>
            {row.map((val, j) => (
              <Tooltip key={j} title={`${columns[i]} × ${columns[j]}: ${val.toFixed(4)}`}>
                <Box
                  sx={{
                    width: cellSize,
                    height: cellSize,
                    minWidth: cellSize,
                    bgcolor: getColor(val),
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: size <= 8 ? 9 : 7,
                    cursor: 'default',
                  }}
                >
                  {size <= 10 ? val.toFixed(2) : ''}
                </Box>
              </Tooltip>
            ))}
          </Box>
        ))}
        {/* Legend */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 12, bgcolor: 'rgba(25, 118, 210, 0.78)' }} />
            <Typography variant="caption">-1</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 12, bgcolor: 'rgba(128, 128, 128, 0.1)' }} />
            <Typography variant="caption">0</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 12, bgcolor: 'rgba(211, 47, 47, 0.78)' }} />
            <Typography variant="caption">+1</Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
